import BaseButton from 'components/atoms/baseButton';
import BoxArrowUpTopIcon from 'assets/img/icons/box-arrow-up-top.svg?react';
import { Fragment, useEffect, useReducer, type FC } from 'react';
import { DateFormatter, groupArrayByFieldname } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import DraggableModal from 'components/organisms/draggableModal';
import { makeid } from 'utils/helpers/makeid';

import CallItem from './call-item';
import MessageItem from './message-item';
import ReviewedItem from './reviewed-item';
import s from './styles.module.scss';
import ThresholdCell from '../threshold-cell';
import ReadingCell from '../reading-cell';

import type { IPatientThresholds } from 'infrastructure/interfaces';
import type {
  IActivityComments,
  TStatuses,
  TThresholdName,
  Value,
} from 'infrastructure/interfaces/i-rpm-patient-activity';
import type { Nullable } from 'infrastructure/types';
import type { ReadingStatus, ReadingType } from 'infrastructure/enums';

const erroneousCommentGuid = 'erroneous-comment-guid';

interface CommentsCellProps {
  comments: IActivityComments[];
  patientGuid: string;
  title: string;
  subtitle: string;
  values: Value;
  statuses: TStatuses;
  readingType: ReadingType;
  globalStatus: ReadingStatus;
  thresholds: IPatientThresholds;
  thresholdName?: Nullable<TThresholdName>;
  page: number;
}

const CommentsCell: FC<CommentsCellProps> = ({
  comments,
  patientGuid,
  title,
  subtitle,
  values,
  statuses,
  readingType,
  globalStatus,
  thresholds,
  thresholdName,
  page,
}) => {
  const [open, toggle] = useReducer((v) => !v, false);

  const sortedCommentsByDate = comments.sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();

    if (dateA < dateB) return -1;
    if (dateA > dateB) return 1;

    return 0;
  });

  const commentsCount = sortedCommentsByDate.length;
  const commentWithFormattedDate = sortedCommentsByDate.map((comment) => ({
    ...comment,
    formattedCreateAt: DateFormatter({
      date: comment.createdAt,
      format: DateFormats['MMM d, yyyy'],
    }),
  }));
  const groupedComments = groupArrayByFieldname(
    commentWithFormattedDate,
    'formattedCreateAt',
  );

  useEffect(() => {
    if (open) toggle();
  }, [page]);

  return (
    <div>
      <BaseButton
        label={`${commentsCount} Comments`}
        type="secondary"
        rightIcon={
          <div className={s['comment-icon-wrapper']}>
            <BoxArrowUpTopIcon className={s['comment-icon']} />
          </div>
        }
        className={s['comment-btn']}
        onClick={toggle}
        disabled={!commentsCount}
      />
      {open && (
        <DraggableModal
          title={title}
          subTitle={subtitle}
          isOpen={open}
          toggle={toggle}
          footer={false}
          titleClassName={s['modal-title']}
          subTitleClassName={s['modal-subtitle']}
          withStackOffset
          subHeader={
            <div className={s['sub-header']}>
              <ReadingCell
                values={values}
                statuses={statuses}
                readingType={readingType}
              />
              <ThresholdCell
                values={values}
                statuses={statuses}
                readingType={readingType}
                globalStatus={globalStatus}
                thresholds={thresholds}
                thresholdName={thresholdName}
              />
            </div>
          }
        >
          <ul data-cy="comment-list" className={s['comments-list']}>
            {Object.keys(groupedComments).map((date) => {
              const commentsOfDate = groupedComments[date];

              return (
                <Fragment key={makeid(5)}>
                  <li
                    data-cy="comments-day"
                    className={s['comments-day']}
                    key={date + makeid(5)}
                  >
                    <p>{date}</p>
                  </li>
                  {commentsOfDate.map((comment) => {
                    const isTypeText = comment.type === 'text';
                    const isMessageNull = comment.message === null;
                    const isTypeCall = comment.type === 'call';
                    const isMockComment = comment.guid.startsWith('id_');
                    const isErroneousComment =
                      comment.guid === erroneousCommentGuid;

                    return (
                      <Fragment key={makeid(5)}>
                        {isTypeText && isMessageNull && (
                          <ReviewedItem
                            comment={comment}
                            key={comment.guid + makeid(5)}
                          />
                        )}
                        {isTypeText && !isMessageNull && (
                          <MessageItem
                            comment={comment}
                            isMockComment={isMockComment}
                            key={comment.guid + +makeid(5)}
                            isErroneousComment={isErroneousComment}
                          />
                        )}
                        {isTypeCall && (
                          <CallItem
                            comment={comment}
                            patientGuid={patientGuid}
                            key={comment.guid + makeid(5)}
                          />
                        )}
                      </Fragment>
                    );
                  })}
                </Fragment>
              );
            })}
          </ul>
        </DraggableModal>
      )}
    </div>
  );
};
export default CommentsCell;
